import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { type PieceWithoutId } from '../types/Piece.js'
import { usePieceStatusDescription } from './usePieceStatusDescription.js'

export type PieceAddressDescriptionFn = (piece: PieceWithoutId) => string | undefined

export const usePieceAddressDescription = (): PieceAddressDescriptionFn => {
  const getPieceStatusDescription = usePieceStatusDescription()
  const { t } = useTranslation()

  return useCallback<PieceAddressDescriptionFn>(
    (piece) => {
      const { destination, receiver, isReturn, isUndeliverable } = piece
      const dest = destination ?? receiver

      if (isReturn || isUndeliverable) {
        return getPieceStatusDescription(piece)
      }

      if (!dest || !dest.address?.houseNumber) {
        return undefined
      }

      const { address, locationType, name, profileAddressName } = dest
      const { street, houseNumber = '', houseNumberSuffix = '' } = address
      const addressLine = street ? `${street} ${houseNumber} ${houseNumberSuffix}` : ''

      switch (locationType) {
        case 'NEIGHBOUR':
          return t('pieces.shipmentDetail.deliverAtTheNeighbours', {
            houseNumber,
            houseNumberSuffix,
          })
        case 'AGREEDPLACE':
          return t('pieces.shipmentDetail.atAgreedPlace')
        case 'SERVICEPOINT':
        case 'PARCELSTATION':
          return name ?? addressLine
        case 'ADDRESS':
          return profileAddressName || addressLine
        default:
          return profileAddressName || addressLine
      }
    },
    [getPieceStatusDescription, t]
  )
}
